import React, { useState } from "react";
import { deleteKnowledgeExcerpt, listOllamaCollections, updateKnowledgeExcerpt, uploadKnowledge } from "../api";

export default function KnowledgeUpload() {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [uploadMode, setUploadMode] = useState("DISTIL");
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [collections, setCollections] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [filterTokens, setFilterTokens] = useState([]);
  const [filterInput, setFilterInput] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [unitingIds, setUnitingIds] = useState(false);
  const [editDraft, setEditDraft] = useState({
    title: "",
    summary: "",
    text: "",
    keywords: "",
  });

  const normalizeCollections = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.collections)) return payload.collections;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const decodeEscapedOctal = (input) => {
    if (!input || typeof input !== "string") return "";
    const bytes = [];

    for (let i = 0; i < input.length; i += 1) {
      const char = input[i];
      if (char === "\\" && /^[0-7]{3}$/.test(input.slice(i + 1, i + 4))) {
        bytes.push(parseInt(input.slice(i + 1, i + 4), 8));
        i += 3;
      } else {
        const code = input.charCodeAt(i);
        bytes.push(code < 256 ? code : 63);
      }
    }

    try {
      return new TextDecoder("utf-8").decode(new Uint8Array(bytes));
    } catch (error) {
      return input;
    }
  };

  const normalizeEscapedText = (input) => {
    if (typeof input !== "string") return input;
    return input
      .replace(/\\\\r\\\\n/g, "\n")
      .replace(/\\\\n/g, "\n")
      .replace(/\\\\r/g, "\n")
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\n")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .replace(/\\\\:/g, ":")
      .replace(/\\:/g, ":")
      .replace(/\\\\'/g, "'")
      .replace(/\\'/g, "'")
      .replace(/\\\\"/g, '"')
      .replace(/\\"/g, '"');
  };

  const extractField = (source, fieldName) => {
    const escapedField = fieldName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const match = source.match(new RegExp(`${escapedField}=string_value:\\s*"((?:\\\\.|[^"\\\\])*)"(?:,|\\n|$)`));
    return match?.[1] || "";
  };

  const parsePayloadRecord = (rawPayload) => {
    if (typeof rawPayload !== "string") return null;

    const payload = rawPayload.startsWith("Payload:") ? rawPayload : `Payload: ${rawPayload}`;
    const timestampMatch = payload.match(/timestamp=integer_value:\s*(\d+)/);
    const keywordsSectionMatch = payload.match(/keywords=list_value[\s\S]*?(?:,\s*(?:title|cleanText|summary)=|$)/);
    const keywordMatches = keywordsSectionMatch?.[0]
      ? [...keywordsSectionMatch[0].matchAll(/string_value:\s*"((?:\\.|[^"\\])*)"/g)]
      : [];

    return {
      timestamp: timestampMatch?.[1] || "",
      title: normalizeEscapedText(decodeEscapedOctal(extractField(payload, "title"))),
      cleanText: normalizeEscapedText(decodeEscapedOctal(extractField(payload, "cleanText"))),
      summary: normalizeEscapedText(decodeEscapedOctal(extractField(payload, "summary"))),
      keywords: keywordMatches.map(match => normalizeEscapedText(decodeEscapedOctal(match[1]))).filter(Boolean),
    };
  };

  const normalizeKeywords = (rawKeywords) => {
    if (!rawKeywords) return [];

    if (Array.isArray(rawKeywords)) {
      return rawKeywords
        .map(keyword => {
          if (typeof keyword === "string") return keyword;
          if (typeof keyword?.string_value === "string") return keyword.string_value;
          if (typeof keyword?.value === "string") return keyword.value;
          return "";
        })
        .map(value => normalizeEscapedText(decodeEscapedOctal(value)))
        .filter(Boolean);
    }

    if (typeof rawKeywords === "object") {
      const values = rawKeywords?.values || rawKeywords?.list_value?.values;
      if (Array.isArray(values)) {
        return normalizeKeywords(values);
      }
    }

    return [];
  };

  const getExcerptText = (item) => {
    const directText = item?.text
      || item?.excerpt
      || item?.content
      || item?.body
      || item?.document
      || item?.chunk
      || item?.value;

    if (typeof directText === "string") return directText;

    if (typeof item?.excerpt === "object" && item?.excerpt) {
      return item.excerpt.text || item.excerpt.content || item.excerpt.body || "";
    }

    if (typeof item?.payload === "object" && item?.payload) {
      return item.payload.text || item.payload.content || item.payload.body || "";
    }

    return "";
  };

  const getRecordData = (item, index) => {
    const itemId = item?.id ?? item?.excerptId ?? item?._id;
    const rawPayload = typeof item === "string"
      ? item
      : (typeof item?.payload === "string" ? item.payload : item?.rawPayload);

    const parsed = parsePayloadRecord(rawPayload) || {};

    const itemTitle = normalizeEscapedText(parsed.title || item?.title || item?.name || `Excerpt #${itemId || index + 1}`);
    const itemText = normalizeEscapedText(parsed.cleanText || getExcerptText(item));
    const summary = normalizeEscapedText(parsed.summary || item?.summary || "");
    const fallbackKeywords = normalizeKeywords(item?.keywords || item?.payload?.keywords);
    const keywords = parsed.keywords?.length ? parsed.keywords : fallbackKeywords;
    const timestamp = parsed.timestamp || item?.timestamp || "";

    return {
      itemId,
      itemTitle,
      itemText,
      summary,
      keywords,
      timestamp,
    };
  };

  const handleUpload = async () => {
    if (!file && !text) return;

    if (file) {
      const res = await uploadKnowledge(file, title, uploadMode);
      alert("Feltöltve: " + res.status);
      return;
    }

    const parts = text
      .split("----")
      .map(part => part.trim())
      .filter(Boolean);

    if (parts.length === 0) return;

    for (let i = 0; i < parts.length; i += 1) {
      const chunk = parts[i];
      const uploadFile = new Blob([chunk], { type: "text/plain" });
      uploadFile.name = `knowledge-${i + 1}.txt`;

      const partTitle = parts.length > 1
        ? (title ? `${title} (${i + 1}/${parts.length})` : `Knowledge ${i + 1}/${parts.length}`)
        : title;

      await uploadKnowledge(uploadFile, partTitle, uploadMode);
    }

    alert(`Feltoltve ${parts.length} resz.`);
  };

  const handleLoadCollections = async () => {
    setLoadingCollections(true);
    try {
      const result = await listOllamaCollections();
      setCollections(normalizeCollections(result));
    } catch (error) {
      alert("Collection betoltese sikertelen: " + (error?.message || "Ismeretlen hiba"));
    } finally {
      setLoadingCollections(false);
    }
  };

  const handleDeleteExcerpt = async (id) => {
    if (!id) return;
    setDeletingId(id);
    try {
      await deleteKnowledgeExcerpt(id);
      setCollections(prev => prev.filter(item => {
        const candidateId = item?.id ?? item?.excerptId ?? item?._id;
        return String(candidateId) !== String(id);
      }));
    } catch (error) {
      alert("Torles sikertelen: " + (error?.message || "Ismeretlen hiba"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleStartEdit = (record) => {
    setEditingId(record.itemId);
    setEditDraft({
      title: record.itemTitle || "",
      summary: record.summary || "",
      text: record.itemText || "",
      keywords: Array.isArray(record.keywords) ? record.keywords.join(", ") : "",
    });
  };

  const toggleSelectId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(String(id))) {
        next.delete(String(id));
      } else {
        next.add(String(id));
      }
      return next;
    });
  };

  const handleUniteSelected = async () => {
    if (selectedIds.size < 2) return;

    const selectedItems = collections
      .map((item, index) => ({ data: getRecordData(item, index) }))
      .filter(({ data }) => selectedIds.has(String(data.itemId)));

    if (selectedItems.length < 2) return;

    const [first, ...rest] = selectedItems;
    const mergedText = selectedItems.map(({ data }) => data.itemText).filter(Boolean).join("\n\n----\n\n");
    const mergedSummary = selectedItems.map(({ data }) => data.summary).filter(Boolean).join("\n");
    const mergedKeywords = [...new Set(selectedItems.flatMap(({ data }) => data.keywords))];

    const payload = {
      title: first.data.itemTitle,
      summary: mergedSummary,
      cleanText: mergedText,
      text: mergedText,
      keywords: mergedKeywords,
    };

    setUnitingIds(true);
    try {
      await updateKnowledgeExcerpt(first.data.itemId, payload);
      for (const { data } of rest) {
        await deleteKnowledgeExcerpt(data.itemId);
      }
      setSelectedIds(new Set());
      await handleLoadCollections();
    } catch (error) {
      alert("Összevonás sikertelen: " + (error?.message || "Ismeretlen hiba"));
    } finally {
      setUnitingIds(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setSavingId(null);
    setEditDraft({
      title: "",
      summary: "",
      text: "",
      keywords: "",
    });
  };

  const handleSaveEdit = async (id) => {
    if (!id) return;

    const keywords = editDraft.keywords
      .split(",")
      .map(keyword => keyword.trim())
      .map(keyword => normalizeEscapedText(keyword))
      .filter(Boolean);

    const payload = {
      title: normalizeEscapedText(editDraft.title.trim()),
      summary: normalizeEscapedText(editDraft.summary.trim()),
      cleanText: normalizeEscapedText(editDraft.text),
      text: normalizeEscapedText(editDraft.text),
      keywords,
    };

    setSavingId(id);
    try {
      await updateKnowledgeExcerpt(id, payload);
      await handleLoadCollections();
      handleCancelEdit();
    } catch (error) {
      alert("Frissites sikertelen: " + (error?.message || "Ismeretlen hiba"));
      setSavingId(null);
    }
  };

  const highlightTokens = (text, tokens) => {
    if (!tokens || tokens.length === 0 || !text) return text;
    const escaped = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const regex = new RegExp(`(${escaped.join("|")})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} style={{ background: "#facc15", color: "#1e1e1e", borderRadius: "2px", padding: "0 1px" }}>{part}</mark>
        : part
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const asNumber = Number(timestamp);
    if (!Number.isFinite(asNumber)) return String(timestamp);
    return new Date(asNumber).toLocaleString();
  };

  return (
    <section className="knowledge-panel">
      <div className="knowledge-panel__header">
        <h3>Knowledge Manager</h3>
        <p>Upload text or file content, then review and maintain excerpt collections.</p>
      </div>

      <div className="knowledge-panel__controls">
        <input
          type="file"
          className="knowledge-panel__file"
          onChange={e => setFile(e.target.files[0])}
        />
        <input
          type="text"
          className="knowledge-panel__title"
          placeholder="Cím"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />
        <fieldset className="knowledge-panel__mode" aria-label="Upload mode">
          <legend>Mode</legend>
          <label className="knowledge-panel__mode-option">
            <input
              type="radio"
              name="uploadMode"
              value="DISTIL"
              checked={uploadMode === "DISTIL"}
              onChange={e => setUploadMode(e.target.value)}
            />
            <span>DISTIL</span>
          </label>
          <label className="knowledge-panel__mode-option">
            <input
              type="radio"
              name="uploadMode"
              value="RAW"
              checked={uploadMode === "RAW"}
              onChange={e => setUploadMode(e.target.value)}
            />
            <span>RAW</span>
          </label>
        </fieldset>
      </div>
      <textarea
        className="knowledge-panel__textarea"
        placeholder="Többsoros szöveg feltöltése (opcionális)"
        value={text}
        onChange={e => setText(e.target.value)}
        rows={6}
      />

      <div className="knowledge-panel__actions">
        <button onClick={handleUpload}>Feltöltés</button>
        <button className="knowledge-panel__action-alt" onClick={handleLoadCollections} disabled={loadingCollections}>
          {loadingCollections ? "Betoltes..." : "Collections megjelenitese"}
        </button>
      </div>

      {collections.length > 0 && (
        <div className="knowledge-panel__list-wrap">
          <div className="knowledge-panel__list-header">
            <h4>Collections</h4>
            <span>{collections.length} records</span>
          </div>

          <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "8px", flexWrap: "wrap" }}>
            <div
              style={{ flex: "1 1 220px", display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center", padding: "3px 6px", borderRadius: "4px", border: "1px solid rgba(255,255,255,.25)", background: "rgba(0,0,0,.2)", cursor: "text", minHeight: "32px" }}
              onClick={e => { if (e.target === e.currentTarget) { const inp = e.currentTarget.querySelector("input"); if (inp) inp.focus(); } }}
            >
              {filterTokens.map(token => (
                <span key={token} style={{ display: "inline-flex", alignItems: "center", gap: "3px", background: "rgba(96,165,250,.25)", border: "1px solid rgba(96,165,250,.5)", borderRadius: "3px", padding: "1px 5px", fontSize: ".82rem", lineHeight: 1.3, whiteSpace: "nowrap" }}>
                  {token}
                  <button
                    type="button"
                    onClick={() => setFilterTokens(prev => prev.filter(t => t !== token))}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: "0 1px", lineHeight: 1, fontSize: ".85rem", opacity: .7 }}
                    aria-label={`Remove filter ${token}`}
                  >&times;</button>
                </span>
              ))}
              <input
                type="text"
                value={filterInput}
                placeholder={filterTokens.length === 0 ? "Filter… (Enter or comma to add token)" : ""}
                onChange={e => setFilterInput(e.target.value)}
                onKeyDown={e => {
                  if ((e.key === "Enter" || e.key === "," || e.key === " ") && filterInput.trim()) {
                    e.preventDefault();
                    const newToken = filterInput.trim().toLowerCase().replace(/,+$/, "");
                    if (newToken && !filterTokens.includes(newToken)) {
                      setFilterTokens(prev => [...prev, newToken]);
                    }
                    setFilterInput("");
                  } else if (e.key === "Backspace" && !filterInput && filterTokens.length > 0) {
                    setFilterTokens(prev => prev.slice(0, -1));
                  }
                }}
                onBlur={() => {
                  const newToken = filterInput.trim().toLowerCase().replace(/,+$/, "");
                  if (newToken && !filterTokens.includes(newToken)) {
                    setFilterTokens(prev => [...prev, newToken]);
                    setFilterInput("");
                  }
                }}
                style={{ flex: "1 1 80px", minWidth: "60px", background: "none", border: "none", outline: "none", color: "inherit", padding: "1px 2px", fontSize: ".9rem" }}
              />
            </div>
            {selectedIds.size >= 2 && (
              <button type="button" disabled={unitingIds} onClick={handleUniteSelected} style={{ whiteSpace: "nowrap" }}>
                {unitingIds ? "Összevonás..." : `Unite selected (${selectedIds.size})`}
              </button>
            )}
            {filterTokens.length > 0 && (
              <button type="button" onClick={() => { setFilterTokens([]); setFilterInput(""); }} style={{ whiteSpace: "nowrap" }}>Clear filter</button>
            )}
            {selectedIds.size > 0 && (
              <button type="button" onClick={() => setSelectedIds(new Set())} style={{ whiteSpace: "nowrap" }}>Deselect all</button>
            )}
          </div>

          <div className="knowledge-panel__list">
            {(() => {
              return (filterTokens.length > 0
                ? collections.filter((item, index) => {
                  const { itemTitle, summary, keywords, itemText } = getRecordData(item, index);
                  const haystack = [
                    itemTitle.toLowerCase(),
                    summary.toLowerCase(),
                    itemText.toLowerCase(),
                    ...keywords.map(kw => kw.toLowerCase()),
                  ];
                  return filterTokens.every(token =>
                    haystack.some(field => field.includes(token))
                  );
                })
                : collections
              ).map((item, index) => {
              const { itemId, itemTitle, itemText, summary, keywords, timestamp } = getRecordData(item, index);
              const timestampLabel = formatTimestamp(timestamp);
              const isEditing = editingId === itemId;
              const isSaving = savingId === itemId;

                const isSelected = selectedIds.has(String(itemId));
                const activeTokens = filterTokens.length > 0 ? filterTokens : [];

              return (
                <article key={itemId || index} className="knowledge-card" style={{ outline: isSelected ? "2px solid #60a5fa" : "none" }}>
                  <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                    {itemId && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${itemTitle}`}
                        checked={isSelected}
                        onChange={() => toggleSelectId(itemId)}
                        style={{ marginTop: "3px", flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editDraft.title}
                      onChange={e => {
                        const value = e.target.value;
                        setEditDraft(prev => ({ ...prev, title: value }));
                      }}
                      placeholder="Title"
                    />
                  ) : (
                          <div className="knowledge-card__title">{activeTokens.length ? highlightTokens(itemTitle, activeTokens) : itemTitle}</div>
                  )}
                  {itemId && <div className="knowledge-card__meta">ID: {itemId}</div>}
                  {timestampLabel && <div className="knowledge-card__meta">Date: {timestampLabel}</div>}
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        className="knowledge-card__keywords-input"
                        value={editDraft.keywords}
                        onChange={e => {
                          const value = e.target.value;
                          setEditDraft(prev => ({ ...prev, keywords: value }));
                        }}
                        placeholder="Keywords (comma separated)"
                        style={{ marginTop: "6px" }}
                      />
                      <textarea
                        value={editDraft.summary}
                        onChange={e => {
                          const value = e.target.value;
                          setEditDraft(prev => ({ ...prev, summary: value }));
                        }}
                        placeholder="Summary"
                        rows={3}
                        style={{ marginTop: "6px", width: "100%" }}
                      />
                      <textarea
                        value={editDraft.text}
                        onChange={e => {
                          const value = e.target.value;
                          setEditDraft(prev => ({ ...prev, text: value }));
                        }}
                        placeholder="Excerpt text"
                        rows={6}
                        style={{ marginTop: "6px", width: "100%" }}
                      />
                      <div style={{ marginTop: "8px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button
                          disabled={!itemId || isSaving}
                          onClick={() => handleSaveEdit(itemId)}
                        >
                          {isSaving ? "Mentes..." : "Save"}
                        </button>
                        <button
                          type="button"
                          disabled={isSaving}
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {keywords.length > 0 && (
                        <div className="knowledge-card__keywords">
                          {keywords.map(keyword => (
                            <span key={`${itemId || index}-${keyword}`} className="knowledge-card__tag">{activeTokens.length ? highlightTokens(keyword, activeTokens) : keyword}</span>
                          ))}
                        </div>
                      )}
                      {summary && (
                              <div className="knowledge-card__summary">{activeTokens.length ? highlightTokens(summary, activeTokens) : summary}</div>
                      )}
                      <div className="knowledge-card__text">
                              {activeTokens.length ? highlightTokens(itemText || "(No excerpt text)", activeTokens) : (itemText || "(No excerpt text)")}
                      </div>
                      <div style={{ marginTop: "8px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          disabled={!itemId}
                          onClick={() => handleStartEdit({ itemId, itemTitle, itemText, summary, keywords })}
                        >
                          Edit
                        </button>
                        <button
                          className="knowledge-card__delete"
                          disabled={!itemId || deletingId === itemId}
                          onClick={() => handleDeleteExcerpt(itemId)}
                        >
                          {deletingId === itemId ? "Torles..." : "Delete excerpt"}
                        </button>
                      </div>
                    </>
                  )}
                    </div>
                  </div>
                </article>
              );
              })
            })()}
          </div>
        </div>
      )}
    </section>
  );
}